import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import io from 'socket.io-client';
import Header from './Header';
import './App.css';

const API_BASE = window.location.origin;
const SOCKET_URL = window.location.origin;

function DevDetails() {
  const { creator } = useParams();
  const navigate = useNavigate();
  const [devData, setDevData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false); // Für 404 (Cache wird generiert)
  const [retryCount, setRetryCount] = useState(0);
  const [sortBy, setSortBy] = useState('age');
  const [sortOrder, setSortOrder] = useState('desc'); // Desc = neueste zuerst
  const [currentPage, setCurrentPage] = useState(1);
  const [socket, setSocket] = useState(null);
  const [searchWallet, setSearchWallet] = useState('');
  const itemsPerPage = 50;

  useEffect(() => {
    loadDevData();
    const newSocket = setupSocket();

    // Polling-Intervall als Fallback (alle 30 Sekunden)
    const pollInterval = setInterval(() => {
      loadDevData(false); // Kein Loading-Spinner beim Polling
    }, 30000);

    return () => {
      if (newSocket) {
        newSocket.disconnect();
      }
      clearInterval(pollInterval);
    };
  }, [creator]);

  // Socket Setup für Echtzeit-Updates
  useEffect(() => {
    if (socket) {
      socket.on('connect', () => {
        console.log('[DEV DETAILS] ✅ Socket connected');
      });

      socket.on('disconnect', () => {
        console.log('[DEV DETAILS] ❌ Socket disconnected');
      });

      // Höre auf neue Coins für diesen Dev
      socket.on('new-dev-launch', (newLaunches) => {
        // Prüfe ob neue Launches von diesem Dev sind
        const relevantLaunches = newLaunches.filter(launch => 
          launch.creator === creator || launch.creator === decodeURIComponent(creator)
        );
        
        if (relevantLaunches.length > 0) {
          console.log(`[DEV DETAILS] 🚀 ${relevantLaunches.length} neue Coins für diesen Dev - lade Daten neu`);
          // Lade Daten neu, um neue Coins zu sehen
          loadDevData();
        }
      });

      return () => {
        socket.off('connect');
        socket.off('disconnect');
        socket.off('new-dev-launch');
      };
    }
  }, [socket, creator]);

  const setupSocket = () => {
    const newSocket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });
    setSocket(newSocket);
    return newSocket;
  };

  const loadDevData = async (showLoading = true) => {
    try {
      setError(null);
      setIsGenerating(false);
      if (showLoading) {
        setLoading(true);
      }
      console.log('[DEV DETAILS] Loading dev data for:', creator);
      const response = await axios.get(`${API_BASE}/api/dev/${encodeURIComponent(creator)}?t=${Date.now()}`);
      console.log('[DEV DETAILS] Response data:', response.data);
      setDevData(response.data);
      setLoading(false);
      setIsGenerating(false);
      setRetryCount(0); // Reset retry count on success
    } catch (err) {
      console.error('[API] Error loading dev data:', err);
      console.error('[API] Error details:', err.response?.data || err.message);
      
      // Prüfe ob es ein 404 ist (Cache wird generiert)
      if (err.response?.status === 404) {
        const errorData = err.response?.data;
        if (errorData?.error === 'Dev data not found. Cache is being generated...') {
          setIsGenerating(true);
          setLoading(false);
          setError(null);
          
          // Auto-Retry nach 3 Sekunden (max 10 Versuche)
          if (retryCount < 10) {
            setTimeout(() => {
              setRetryCount(prev => prev + 1);
              loadDevData(false); // Kein Loading-Spinner beim Retry
            }, 3000);
          }
          return;
        }
      }
      
      // Andere Fehler
      setError(err.message || 'Failed to load dev data');
      setLoading(false);
      setIsGenerating(false);
    }
  };

  const formatNumber = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return 'N/A';
    if (num >= 1000000) {
      return (num / 1000000).toFixed(2) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(2) + 'K';
    }
    return num.toFixed(2);
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = typeof timestamp === 'number' ? new Date(timestamp) : new Date(timestamp);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + " years ago";
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + " months ago";
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + " days ago";
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + " hours ago";
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + " min ago";
    return Math.floor(seconds) + " seconds ago";
  };

  const handleSearch = () => {
    const wallet = searchWallet.trim();
    if (wallet) {
      navigate(`/dev/${encodeURIComponent(wallet)}`);
      setSearchWallet(''); // Clear search after navigation
    }
  };

  const getSortedCoins = () => {
    if (!devData || !devData.coins || !Array.isArray(devData.coins)) return [];
    
    const sorted = [...devData.coins].sort((a, b) => {
      let aValue, bValue;
      
      if (sortBy === 'age') {
        // Age: Timestamp (höher = neuer)
        aValue = a.created_at_timestamp || new Date(a.created_at).getTime();
        bValue = b.created_at_timestamp || new Date(b.created_at).getTime();
        // Desc = neueste zuerst (höchster Timestamp zuerst)
        if (sortOrder === 'desc') {
          return bValue - aValue;
        } else {
          return aValue - bValue;
        }
      } else if (sortBy === 'peakmarketcapsol') {
        aValue = parseFloat(a.peakmarketcapsol_usd || 0);
        bValue = parseFloat(b.peakmarketcapsol_usd || 0);
      } else if (sortBy === 'currentmarketcapsol') {
        aValue = parseFloat(a.currentmarketcapsol_usd || 0);
        bValue = parseFloat(b.currentmarketcapsol_usd || 0);
      } else {
        return 0;
      }
      
      if (sortOrder === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });
    
    return sorted;
  };

  const sortedCoins = getSortedCoins();
  const totalPages = Math.ceil(sortedCoins.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCoins = sortedCoins.slice(startIndex, endIndex);

  // Zeige schöne Lade-Seite wenn Cache generiert wird (404)
  if (isGenerating) {
    return (
      <div className="App">
        <Header />
        <main className="app-main">
          <div className="dev-details-container">
            <div className="generating-cache-container">
              <div className="generating-icon">⏳</div>
              <h2>Dev Stats werden erstellt...</h2>
              <p className="generating-message">
                Die Statistiken für diesen Developer werden gerade generiert.
                <br />
                Dies dauert normalerweise nur wenige Sekunden.
              </p>
              {retryCount > 0 && (
                <p className="retry-info">
                  Versuch {retryCount} von 10...
                </p>
              )}
              <div className="generating-actions">
                <button 
                  onClick={() => {
                    setRetryCount(0);
                    loadDevData(true);
                  }} 
                  className="retry-btn"
                >
                  🔄 Jetzt neu laden
                </button>
                <button 
                  onClick={() => navigate('/')} 
                  className="back-btn"
                >
                  ← Zurück zur Startseite
                </button>
              </div>
              <div className="loading-spinner-small"></div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="App">
        <Header />
        <main className="app-main">
          <div className="dev-details-container">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading Dev Details...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error || !devData) {
    return (
      <div className="App">
        <Header />
        <main className="app-main">
          <div className="dev-details-container">
            <div className="error-container">
              <h2>❌ Fehler beim Laden</h2>
              <p>{error || 'Dev not found'}</p>
              <div className="error-actions">
                <button onClick={() => loadDevData(true)} className="retry-btn">
                  🔄 Erneut versuchen
                </button>
                <button onClick={() => navigate('/')} className="back-btn">
                  ← Zurück zur Startseite
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Sicherstellen, dass stats existiert
  const stats = devData.stats || {};
  const coins = devData.coins || [];

  return (
    <div className="App">
      <Header />

      <main className="app-main">
        <div className="dev-details-container">
          <div className="dev-stats-card">
            <div className="dev-header">
              <h2>Dev Wallet</h2>
              <p className="dev-wallet-address">{devData.creator || creator}</p>
            </div>
            
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-label">Total Launches</span>
                <span className="stat-value highlight">{stats.totallaunches || 0}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Bonded Count</span>
                <span className="stat-value highlight">{stats.bondedcount || 0}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Bonding Rate</span>
                <span className="stat-value highlight neon-green">{stats.bonding_rate_percent || '0.00'}%</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Avg Peak MC (Not Bonded)</span>
                <span className="stat-value highlight neon-gold">${formatNumber(stats.avgpeakmarketcapnotbonded_usd || 0)}</span>
              </div>
            </div>
          </div>

          <div className="coins-table-container">
            <div className="table-header">
              <h3>All Coins ({sortedCoins.length})</h3>
              <div className="table-controls">
                <div className="sort-controls">
                  <label>Sort by:</label>
                  <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sort-select">
                    <option value="age">Age</option>
                    <option value="peakmarketcapsol">Peak Market Cap</option>
                    <option value="currentmarketcapsol">Current Market Cap</option>
                  </select>
                  <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} className="sort-select">
                    <option value="desc">Desc</option>
                    <option value="asc">Asc</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Desktop Table */}
            <div className="table-wrapper desktop-layout">
              <table className="coins-table">
                <thead>
                  <tr>
                    <th>Created</th>
                    <th>Symbol</th>
                    <th>Name</th>
                    <th>DEX Status</th>
                    <th>Current MC</th>
                    <th>Peak MC</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedCoins.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                        No coins found
                      </td>
                    </tr>
                  ) : (
                    paginatedCoins.map((coin) => (
                      <tr key={coin.tokenmint}>
                        <td className="time-cell">{formatTimeAgo(coin.created_at_timestamp || coin.created_at)}</td>
                        <td><strong className="coin-symbol">{coin.symbol}</strong></td>
                        <td className="coin-name">{coin.name}</td>
                        <td>
                          {(() => {
                            const dexStatus = coin.dex_status;
                            if (dexStatus === 'PENDING' || dexStatus === 'APPROVED' || dexStatus === 'PAID') {
                              return (
                                <div className={`dex-badge-small ${dexStatus === 'APPROVED' || dexStatus === 'PAID' ? 'dex-paid' : 'dex-pending'}`}>
                                  <div>DEX PAID</div>
                                  {coin.dex_payment_time_ago && (
                                    <div className="dex-payment-time">{coin.dex_payment_time_ago}</div>
                                  )}
                                </div>
                              );
                            }
                            return (
                              <div className="dex-badge-small dex-not-paid">
                                DEX NOT PAID
                              </div>
                            );
                          })()}
                        </td>
                        <td className="mc-value-cell">${formatNumber(coin.currentmarketcapsol_usd)}</td>
                        <td className="mc-value-cell peak">${formatNumber(coin.peakmarketcapsol_usd)}</td>
                        <td>
                          <div className="coin-actions">
                            <a 
                              href={`https://pump.fun/coin/${coin.tokenmint}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="action-link"
                            >
                              🚀 Pump.fun
                            </a>
                            <a 
                              href={`https://neo.bullx.io/terminal?chainId=1399811149&address=${coin.tokenmint}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="action-link"
                            >
                              📊 BullX
                            </a>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Mobile Cards */}
            <div className="mobile-layout dev-coins-mobile">
              {paginatedCoins.length === 0 ? (
                <div className="mobile-empty-state">
                  <p>No coins found</p>
                </div>
              ) : (
                paginatedCoins.map((coin) => (
                  <div key={coin.tokenmint} className="dev-coin-mobile-card">
                    <div className="mobile-coin-header">
                      <div className="mobile-coin-symbol">{coin.symbol || 'N/A'}</div>
                      <div className="mobile-coin-time">{formatTimeAgo(coin.created_at_timestamp || coin.created_at)}</div>
                    </div>
                    
                    <div className="mobile-coin-name">{coin.name || 'N/A'}</div>
                    
                    <div 
                      className="mobile-coin-tokenmint clickable"
                      onClick={() => {
                        navigator.clipboard.writeText(coin.tokenmint);
                      }}
                      title="Click to copy"
                    >
                      {coin.tokenmint}
                    </div>
                    
                    <div className="mobile-coin-mc-row">
                      <div className="mobile-coin-mc-item">
                        <div className="mobile-coin-mc-label">Current MC</div>
                        <div className="mobile-coin-mc-value">${formatNumber(coin.currentmarketcapsol_usd)}</div>
                      </div>
                      <div className="mobile-coin-mc-item">
                        <div className="mobile-coin-mc-label">Peak MC</div>
                        <div className="mobile-coin-mc-value peak">${formatNumber(coin.peakmarketcapsol_usd)}</div>
                      </div>
                    </div>
                    
                    <div className="mobile-coin-dex">
                      {(() => {
                        const dexStatus = coin.dex_status;
                        if (dexStatus === 'PENDING' || dexStatus === 'APPROVED' || dexStatus === 'PAID') {
                          return (
                            <div className={`mobile-dex-badge ${dexStatus === 'APPROVED' || dexStatus === 'PAID' ? 'dex-paid' : 'dex-pending'}`}>
                              <div>DEX PAID</div>
                              {coin.dex_payment_time_ago && (
                                <div className="dex-payment-time">{coin.dex_payment_time_ago}</div>
                              )}
                            </div>
                          );
                        }
                        return (
                          <div className="mobile-dex-badge dex-not-paid">
                            DEX NOT PAID
                          </div>
                        );
                      })()}
                    </div>
                    
                    <div className="mobile-coin-actions">
                      <a 
                        href={`https://pump.fun/coin/${coin.tokenmint}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mobile-action-btn pump-btn"
                      >
                        🚀 Pump.fun
                      </a>
                      <a 
                        href={`https://neo.bullx.io/terminal?chainId=1399811149&address=${coin.tokenmint}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mobile-action-btn bullx-btn"
                      >
                        📊 BullX
                      </a>
                    </div>
                  </div>
                ))
              )}
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button 
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="page-btn"
                >
                  ← Prev
                </button>
                <span className="page-info">Page {currentPage} of {totalPages}</span>
                <button 
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="page-btn"
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default DevDetails;

